// react-router v5 API (Switch, Route, Redirect, withRouter, useHistory,
// matchPath) implemented on top of react-router v7, so the skins' route
// tables and pages keep working without being rewritten.
import {
  Children,
  cloneElement,
  createContext,
  createElement,
  forwardRef,
  isValidElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  BrowserRouter as RRBrowserRouter,
  Link,
  matchPath as rrMatchPath,
  UNSAFE_NavigationContext,
  useLocation as useRRLocation,
  useNavigationType,
} from "react-router";

export { Link };

// v5 kept extra fields of a pushed location object (e.g. { pathname, tab });
// they travel in history state under this key
const EXTRA = "__v5extra";

// v5 location.state was undefined when empty, v7 uses null
export function useLocation() {
  const location = useRRLocation();
  return useMemo(() => {
    const { state } = location;
    if (state && state[EXTRA]) {
      return { ...location, ...state[EXTRA], state: state.state };
    }
    return state === null ? { ...location, state: undefined } : location;
  }, [location]);
}

const RouteContext = createContext(null);

let navigator = null;
let currentLocation = null;
const listeners = new Set();

// v5 resolved relative paths ("index") against the current URL
const resolvePath = (path) => {
  if (!path || /^[/?#]/.test(path)) return path;
  const url = new URL(path, window.location.href);
  return url.pathname + url.search + url.hash;
};

// v5 accepted the location state inside the `to` object
const toLocation = (to, state) => {
  if (typeof to === "string") return { to: resolvePath(to), state };
  const { pathname, search, hash, key, state: toState, ...extra } = to;
  const path = {};
  if (pathname) path.pathname = resolvePath(pathname);
  if (search) path.search = search;
  if (hash) path.hash = hash;
  const nextState = toState === undefined ? state : toState;
  return {
    to: path,
    state: Object.keys(extra).length
      ? { [EXTRA]: extra, state: nextState }
      : nextState,
  };
};

// Stable v5-style history singleton, safe to use outside of effects
export const history = {
  get location() {
    return currentLocation;
  },
  get length() {
    return window.history.length;
  },
  push(to, state) {
    const next = toLocation(to, state);
    navigator.push(next.to, next.state);
  },
  replace(to, state) {
    const next = toLocation(to, state);
    navigator.replace(next.to, next.state);
  },
  go: (n) => navigator.go(n),
  goBack: () => navigator.go(-1),
  goForward: () => navigator.go(1),
  listen(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

function HistoryBridge({ children }) {
  const location = useLocation();
  const action = useNavigationType();
  const mounted = useRef(false);
  navigator = useContext(UNSAFE_NavigationContext).navigator;
  currentLocation = location;

  useEffect(() => {
    if (mounted.current) {
      listeners.forEach((listener) => listener(location, action));
      return;
    }
    mounted.current = true;
    // BrowserRouter subscribes in a layout effect, after its children's
    // layout effects; pick up a navigation made before that.
    const { pathname, search, hash } = window.location;
    if (
      pathname + search + hash !==
      location.pathname + location.search + location.hash
    ) {
      navigator.replace({ pathname, search, hash }, window.history.state?.usr);
    }
  }, [location, action]);

  return children;
}

export function BrowserRouter({ children, ...props }) {
  return (
    <RRBrowserRouter {...props}>
      <HistoryBridge>{children}</HistoryBridge>
    </RRBrowserRouter>
  );
}

const trimSlash = (path) => path.replace(/(.)\/+$/, "$1");

export function matchPath(pathname, options = {}) {
  if (typeof options === "string" || Array.isArray(options)) {
    options = { path: options };
  }
  const { path, exact = false, strict = false, sensitive = false } = options;

  return [].concat(path).reduce((matched, p) => {
    if (matched || (!p && p !== "")) return matched;
    // v5 strict: a trailing slash must match exactly
    if (strict && exact && p.endsWith("/") !== pathname.endsWith("/"))
      return null;
    // v5 params could be constrained with a regex, e.g. ":subcategory(a|b)"
    const constraints = {};
    const v7Path = p.replace(/:(\w+)\(([^)]*)\)/g, (_, name, pattern) => {
      constraints[name] = new RegExp(`^(?:${pattern})$`, sensitive ? "" : "i");
      return `:${name}`;
    });
    const match = rrMatchPath(
      { path: v7Path, caseSensitive: sensitive, end: exact },
      pathname,
    );
    if (!match) return null;
    for (const name in constraints) {
      if (!constraints[name].test(match.params[name])) return null;
    }
    const url = p === "/" && match.pathname === "" ? "/" : match.pathname;
    return {
      path: p,
      url,
      isExact: trimSlash(pathname) === trimSlash(url),
      params: match.params,
    };
  }, null);
}

const rootMatch = (pathname) => ({
  path: "/",
  url: "/",
  params: {},
  isExact: pathname === "/",
});

function useRouterProps() {
  const location = useLocation();
  const route = useContext(RouteContext);
  return {
    history,
    location,
    match: route ? route.match : rootMatch(location.pathname),
  };
}

export const useHistory = () => history;

export const useParams = () => useRouterProps().match.params;

export function Switch({ children, location: locationProp }) {
  const context = useRouterProps();
  const location = locationProp || context.location;
  let element;
  let match = null;

  Children.forEach(children, (child) => {
    if (match == null && isValidElement(child)) {
      element = child;
      const path = child.props.path || child.props.from;
      match = path
        ? matchPath(location.pathname, { ...child.props, path })
        : context.match;
    }
  });

  return match
    ? cloneElement(element, { location, computedMatch: match })
    : null;
}

export function Route({
  computedMatch,
  location: locationProp,
  path,
  exact,
  strict,
  sensitive,
  component,
  render,
  children,
}) {
  const context = useRouterProps();
  const location = locationProp || context.location;
  const match = computedMatch
    ? computedMatch
    : path
      ? matchPath(location.pathname, { path, exact, strict, sensitive })
      : context.match;
  const props = { history, location, match };

  let content = null;
  if (typeof children === "function") {
    content = children(props);
  } else if (match) {
    if (Children.count(children) > 0) content = children;
    else if (component) content = createElement(component, props);
    else if (render) content = render(props);
  }

  return <RouteContext.Provider value={props}>{content}</RouteContext.Provider>;
}

export function Redirect({ to, push = false }) {
  const key = JSON.stringify(to);

  useEffect(() => {
    if (push) history.push(to);
    else history.replace(to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return null;
}

// v5 NavLink: activeClassName, activeStyle, exact, strict, isActive(match, location)
export const NavLink = forwardRef(function NavLink(
  {
    to,
    className,
    style,
    activeClassName = "active",
    activeStyle,
    exact,
    strict,
    isActive,
    ...props
  },
  ref,
) {
  const location = useLocation();
  const path = typeof to === "string" ? to : to.pathname;
  const match = path
    ? matchPath(location.pathname, {
        path: path.split(/[?#]/)[0],
        exact,
        strict,
      })
    : null;
  const active = Boolean(isActive ? isActive(match, location) : match);

  return (
    <Link
      ref={ref}
      to={to}
      className={
        [className, active && activeClassName].filter(Boolean).join(" ") ||
        undefined
      }
      style={active ? { ...style, ...activeStyle } : style}
      aria-current={active ? "page" : undefined}
      {...props}
    />
  );
});

export function withRouter(Component) {
  function WithRouter(props) {
    return <Component {...props} {...useRouterProps()} />;
  }
  WithRouter.displayName = `withRouter(${Component.displayName || Component.name})`;
  return WithRouter;
}
